import { CommonModule, LowerCasePipe } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  inject,
  Input,
  OnDestroy,
  OnInit,
  signal,
  ViewChild,
} from '@angular/core';
import { MatIcon } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Router } from '@angular/router';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { ChartData, ChartOptions, ChartType } from 'chart.js';
import { BaseChartDirective } from 'ng2-charts';
import { Subject, Subscription } from 'rxjs';
import { DashboardCard, GraphVariable } from '../../../class/Preference';
import { TripStats, TripstatsData } from '../../../class/TripStats';
import { AccountStateService } from '../../../services/account-state.service';
import { AuthService } from '../../../services/auth.service';
import { DevicesManagerService } from '../../../services/devices-manager.service';
import { DeviceSaveListService } from '../../../services/devicesavelist.service';
import { LoadingService } from '../../../services/loading.service';
import { LocalStorageService } from '../../../services/local-storage.service';
import { PermissionService } from '../../../services/permission.service';
import { PreferenceService } from '../../../services/preference.service';
import { RoutingService } from '../../../services/routing.service';
import { TripstatsStoreService } from '../../../services/tripstats-store.service';
import { TripStatsService } from '../../../services/tripstats.service';
import UnitsService from '../../../services/units.service';
import HasPermissionDirective from '../../../utils/directives/has-permission.directive';
import DynamicDatePipe from '../../../utils/pipes/dynamic-date.pipe';
import { WidgetLoaderComponent } from '../../shared/widget/widget-loader/widget-loader.component';
import { ChartConfigComponent } from '../chart-config/chart-config.component';

@Component({
  selector: 'chart',
  templateUrl: './chart.component.html',
  styleUrls: ['./chart.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    CommonModule,
    ChartConfigComponent,
    MatIcon,
    MatTooltipModule,
    TranslatePipe,
    MatMenuModule,
    DynamicDatePipe,
    WidgetLoaderComponent,
    LowerCasePipe,
    BaseChartDirective,
    HasPermissionDirective,
  ],
})
export class ChartComponent implements OnInit, OnDestroy {
  @Input() config: DashboardCard;
  @Input() index: number;
  @Input() fullscreen: boolean;
  @Input() isEditDash: boolean;

  //Used to refresh chart when changing options
  @ViewChild('baseChart') chart: BaseChartDirective;
  public tripStatsService = inject(TripStatsService);
  public tripstatsStore = inject(TripstatsStoreService);
  private _unitsService = inject(UnitsService);
  private _devicesManagerService = inject(DevicesManagerService);
  private _localStorageService = inject(LocalStorageService);
  private _authService = inject(AuthService);
  private _accountStateService = inject(AccountStateService);
  public routingService = inject(RoutingService);
  public loadingService = inject(LoadingService);
  public preferenceService = inject(PreferenceService);
  public deviceSaveListService = inject(DeviceSaveListService);
  public router = inject(Router);
  public permissionService = inject(PermissionService);
  private translateService = inject(TranslateService);
  private _dynamicDatePipe = inject(DynamicDatePipe);

  public chartOptions: ChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    animation: {
      duration: 0,
    },
    scales: {
      y: {
        ticks: {
          precision: 0,
          callback: function (label, index, labels) {
            return label;
          },
        },
      },
      x: {
        ticks: {
          autoSkip: false,
          display: false,
        },
        grid: {
          display: false,
        },
      },
    },
    plugins: {
      tooltip: {
        callbacks: {
          label: function (tooltipItem) {
            if (tooltipItem.dataset.type === 'line') {
              return '';
            }
            return tooltipItem.parsed.y + '';
          },
          title: function (tooltipItem) {
            return '';
          },
          footer: function (tooltipItem) {
            return '';
          },
        },
        footerFont: { size: 9, style: 'italic' },
        footerColor: '#CECECE',
      },
      legend: {
        display: false,
        position: 'top',
        labels: {
          font: { size: 12 },
          usePointStyle: true,
        },
      },
    },
  };
  public chartData: ChartData<'bar' | 'line'> = {
    labels: [],
    datasets: [],
  };
  public chartLabelsStages = [];
  public chartType: ChartType = 'bar';
  public selectColor: string;
  public chartLegend = false;
  public $isReadyDisplay = signal<boolean>(undefined);
  public from: number;
  public to: number;
  public period: string;
  public changingService = false;
  public noDataSubServices = false;
  public cameFromService = false;
  public noData = false;
  public unit = 'h';

  public $isEditing = this.tripStatsService.$isEditChart;
  private componentDestroy$ = new Subject<void>();

  public labelsGraph = {
    mileage: this.translateService.instant('CHART.MILEAGE'),
    stops: this.translateService.instant('CHART.STOPS'),
    engine: this.translateService.instant('CHART.ENGINE'),
    trip_time: this.translateService.instant('CHART.TRAVEL_TIME'),
    speed_avg: this.translateService.instant('CHART.AVERAGE_SPEED'),
    eco_avg: this.translateService.instant('CHART.ECO_DRIVING_AVG'),
    fuel_quantity_100_km: this.translateService.instant('CHART.FUEL_QUANTITY_100_KM'),
    fuel_cost_100_km: this.translateService.instant('CHART.FUEL_COST_100_KM'),
    CO2_100_km: this.translateService.instant('CHART.CO2_100_KM'),
    fuel_consumed: this.translateService.instant('CHART.FUEL_CONSUMED'),
  };

  public unitsGraph = {
    mileage: ' ' + this.translateService.instant('UNITS.DISTANCE'),
    stops: '',
    engine: ' h',
    trip_time: ' h',
    speed_avg: ' ' + this.translateService.instant('UNITS.SPEED'),
    eco_avg: '',
    fuel_quantity_100_km: ' L/100km',
    fuel_cost_100_km: ' €/100km',
    CO2_100_km: ' CO₂/100km',
    fuel_consumed: ' L',
  };

  private speedUnits = {
    speed: ' km/h',
    speedBoat: ' kn',
  };

  public breaking: {
    device: string;
    service: string;
    month: string;
    week: string;
    day: string;
  } = {
    device: this.translateService.instant('CHART.SENSOR'),
    service: 'Service',
    month: this.translateService.instant('DATE.MONTH'),
    week: this.translateService.instant('DATE.WEEK_2'),
    day: this.translateService.instant('DATE.DAY_2'),
  };

  public timeBreaking: {
    M3: string;
    ML: string;
    MR: string;
    WL: string;
    WR: string;
  } = {
    M3: this.translateService.instant('DATE.LAST_THREE_MONTHS_BIS').toLowerCase(),
    ML: this.translateService.instant('DATE.LAST_MONTH_BIS').toLowerCase(),
    MR: this.translateService.instant('DATE.CURRENT_MONTH_BIS').toLowerCase(),
    WL: this.translateService.instant('DATE.LAST_WEEK_BIS').toLowerCase(),
    WR: this.translateService.instant('DATE.CURRENT_WEEK_BIS').toLowerCase(),
  };

  private allLoadedEventSubscriber: Subscription;

  public colorsGraph: Record<GraphVariable, string> = {
    mileage: 'rgb(46, 170, 221)',
    stops: 'rgb(226, 76, 97)',
    engine: 'rgb(249, 184, 41)',
    trip_time: 'rgb(249, 134, 40)',
    speed_avg: 'rgb(45, 45, 221)',
    eco_avg: 'rgb(167, 219, 138)',
    fuel_quantity_100_km: 'rgb(139, 45, 221)',
    fuel_cost_100_km: 'rgb(45, 221, 171)',
    CO2_100_km: 'rgb(57, 221, 45)',
    fuel_consumed: 'rgb(183,116,102)',
  };

  public colorsGraphAlpha: Record<GraphVariable, string> = {
    mileage: 'rgba(46, 170, 221, 0.4)',
    stops: 'rgba(249, 53, 40, 0.4)',
    engine: 'rgba(249, 184, 41, 0.4)',
    trip_time: 'rgba(249, 134, 40, 0.4)',
    speed_avg: 'rgba(45, 45, 221, 0.4)',
    eco_avg: 'rgba(167, 219, 138, 0.4)',
    fuel_quantity_100_km: 'rgba(139, 45, 221, 0.4)',
    fuel_cost_100_km: 'rgba(45, 221, 171, 0.4)',
    CO2_100_km: 'rgba(57, 221, 45, 0.4)',
    fuel_consumed: 'rgba(183,116,102, 0.4)',
  };

  private grayColor = 'rgba(148,159,177,0.2)';
  public isSortedData = false;
  public $unitsById = this._unitsService.$unitsById;

  ngOnInit() {
    if (
      this._unitsService.$unitsById() &&
      this._devicesManagerService.$devicesMap() &&
      this.preferenceService.$preference()
    ) {
      this.initComponent();
    } else {
      this.allLoadedEventSubscriber = this.loadingService.allLoadedEvent$.subscribe(() => {
        this.initComponent();
        this.allLoadedEventSubscriber.unsubscribe();
      });
    }
  }

  private initComponent() {
    this.unitsGraph.speed_avg = this.speedUnits[this.preferenceService.$preference().config.speedUnit];
    this.setPeriod(true);
  }

  ngOnDestroy() {
    this.componentDestroy$.next();
    this.componentDestroy$.complete();
  }

  public updateDataEvent(event: DashboardCard) {
    this.config = event;
    this.updateData();
  }

  private updateData(init?: boolean) {
    this.setChartGUI();
    this.noData = false;
    this.$isReadyDisplay.set(false);

    if (this.config.y && this.config.period !== '') {
      if (this.config.y === 'eco_avg' && !this._accountStateService.$account().isSubsEco) {
        this.noData = true;
        return;
      }
      const storedData = this.tripstatsStore.$tripstats()[this.index];
      const storedConfig =
        this.preferenceService.$preference().dashboard &&
        this.preferenceService.$preference().dashboard[this.index]
          ? this.preferenceService.$preference().dashboard[this.index]
          : undefined;
      if (this.checkLocalStorage(storedData) && storedConfig && !this.$isEditing()) {
        this.config = storedConfig;
        this.chartData = {
          labels: [],
          datasets: [],
        };
        this.chartLabelsStages = [];
        if (this.config.compare) {
          const dates = this.getParallelPeriod(this.config.period, this.from, this.to);
          const chartStatsCompare = this.selectTripStats(storedData.tripstats.data_compare);
          if (this.unit === '%') {
            chartStatsCompare.engine = this.getPercent(chartStatsCompare);
          }

          this.chartData.datasets.unshift({
            data: chartStatsCompare[this.config.y],
            label: `${this.getDateToString(dates[0])} - ${this.getDateToString(dates[1])}`,
            backgroundColor: this.grayColor,
            borderColor: 'transparent',
          });
          this.chartData.labels = chartStatsCompare[this.getX(this.config.group)];
          this.getFooterOnChart(this.config, chartStatsCompare);
          this.chartLabelsStages.unshift(chartStatsCompare[this.getX(this.config.group)]);
          this.getTitleOnChart(this.chartLabelsStages, this.config);
        }
        const chartStats = this.selectTripStats(storedData);
        if (this.unit === '%') {
          chartStats.engine = this.getPercent(chartStats);
        }

        this.chartData.datasets.push({
          data: chartStats[this.config.y],
          label: `${this.getDateToString(this.from)} - ${this.getDateToString(this.to)}`,
          backgroundColor: this.setColor(this.config.y, chartStats[this.config.y]),
          borderColor: 'transparent',
        });

        this.chartData.labels = chartStats[this.getX(this.config.group)];
        this.getFooterOnChart(this.config, chartStats);
        this.chartLabelsStages.push(chartStats[this.getX(this.config.group)]);
        this.getTitleOnChart(this.chartLabelsStages, this.config);
        this.chartLegend = this.config.compare ? this.config.compare : false; //Can be undefined
        this.chartOptions.plugins.legend.display = this.chartLegend;
        this.$isReadyDisplay.set(true);
      } else {
        this.chartData = {
          labels: [],
          datasets: [],
        };
        let tripstats;
        this.tripStatsService
          .loadTripStats(
            this._authService.$token(),
            this.from,
            this.to,
            this.config.y,
            this.config.group,
            this.config.uid_service
          )
          .subscribe(
            value => {
              tripstats = value;
            },
            error => {
              console.log(error);
            },
            () => {
              this.buildDataToChart(tripstats, this.config, this.index, this.from, this.to, false);
              //store only to localstorage after modifying a widget to prevent multiple data writing for each widget on init
              if (this.config.compare === true) {
                this.addCompareData(this.config, this.from, this.to, this.index);
              }
              if (!init) {
                this.preferenceService.saveDraggedPreferences('dashboard', true, false);
                console.log('store tripstats to localstorage');
              }
            }
          );
        this.chartLegend = this.config.compare ? this.config.compare : false; //Can be undefined
        this.chartOptions.plugins.legend.display = this.chartLegend;
      }
    } else {
      this.chartData = {
        labels: [],
        datasets: [],
      };
      this.chartLabelsStages = [''];
      this.$isReadyDisplay.set(true);
    }
  }

  private checkLocalStorage(tripstatsData: TripstatsData): boolean {
    const today = new Date().getUTCDate();
    if (tripstatsData && tripstatsData.servicedata && tripstatsData.tripstats) {
      const dayLocal = new Date(tripstatsData.tripstats.timestamp * 1000).getUTCDate();
      return today === dayLocal;
    } else {
      return false;
    }
  }

  private buildDataToChart(
    tripstats: TripstatsData,
    config: DashboardCard,
    index: number,
    from: number,
    to: number,
    compare: boolean
  ) {
    const chartStats = this.selectTripStats(tripstats);
    if (this.unit === '%' && config.y === 'engine') {
      chartStats.engine = this.getPercent(chartStats);
    }
    if (compare) {
      this.chartData.datasets.unshift({
        data: chartStats[config.y],
        label: `${this.getDateToString(from)} - ${this.getDateToString(to)}`,
        backgroundColor: this.grayColor,
        borderColor: 'transparent',
      });
      this.tripstatsStore.mutate(ts => {
        ts[index].tripstats.data_compare = tripstats;
      });
      this.chartLabelsStages.unshift(chartStats[this.getX(this.config.group)]);
      this.getTitleOnChart(this.chartLabelsStages, config);
      this.chartLegend = compare;
      this.chartOptions.plugins.legend.display = this.chartLegend;
      this.chart.update();
    } else {
      this.chartLabelsStages = [];
      this.chartData = {
        labels: [],
        datasets: [],
      };
      this.chartData.datasets.push({
        data: chartStats[config.y],
        label: `${this.getDateToString(from)} - ${this.getDateToString(to)}`,
        backgroundColor: this.setColor(this.config.y, chartStats[config.y]),
        borderColor: 'transparent',
      });
      this.tripstatsStore.mutate(ts => {
        ts[index] = tripstats;
      });
      this.chartLabelsStages.push(chartStats[this.getX(this.config.group)]);
      this.getTitleOnChart(this.chartLabelsStages, config);
    }
    this.preferenceService.mutatePreference(preference => {
      preference.dashboard.splice(index, 1, config);
    });
    this._localStorageService.storeElement('tripstats', {
      stats: this.tripstatsStore.$tripstats(),
    });

    if (
      this.chartData.datasets[0].data.length < 1 ||
      (this.chartData.datasets[0].data.length === 1 && this.chartData.datasets[0].data[0] === 0)
    ) {
      this.noData = true;
    }

    this.chartData.labels = chartStats[this.getX(this.config.group)];

    this.getFooterOnChart(config, chartStats);
    this.$isReadyDisplay.set(true);
  }

  public updateCompareData(event: boolean) {
    if (event) {
      this.addCompareData(this.config, this.from, this.to, this.index);
    } else {
      this.chartData.datasets.splice(0, 1);
      this.chartLabelsStages.splice(0, 1);
      this.tripstatsStore.mutate(ts => {
        ts[this.index].tripstats.data_compare = undefined;
      });
      this.chart.update();
    }
    this.chartLegend = event;
    this.chartOptions.plugins.legend.display = this.chartLegend;
    this.preferenceService.saveDraggedPreferences('dashboard', true, false);
    console.log('store tripstats to localstorage');
  }

  public addCompareData(config: DashboardCard, from: number, to: number, index: number) {
    const dates = this.getParallelPeriod(config.period, from, to);
    let tripstatsCompare;
    this.tripStatsService
      .loadTripStats(
        this._authService.$token(),
        dates[0],
        dates[1],
        config.y,
        config.group,
        config.uid_service
      )
      .subscribe(
        value => {
          tripstatsCompare = value;
        },
        error => {
          console.log(error);
        },
        () => {
          this.buildDataToChart(tripstatsCompare, config, index, dates[0], dates[1], config.compare);
        }
      );
  }

  public getService(devicesArray) {
    const servicesArray: string[] = [];
    devicesArray.map(device =>
      servicesArray.push(
        this._unitsService.$unitsById()?.[this._devicesManagerService.get(device)?.uid_parent]?.label
      )
    );
    return servicesArray;
  }

  private selectTripStats(tripData: TripstatsData): TripStats {
    const uid_service: number = this.config.uid_service ? this.config.uid_service : 0;
    if (this.config.group === 'service' && tripData.servicedata[uid_service]) {
      // A per-service entry only carries the fields relevant to the 'service' group
      // (mileage, stops, engine, ... and totals); it is consumed here the same way
      // as a full TripStats, so it's safe to treat it as one.
      return tripData.servicedata[uid_service] as unknown as TripStats;
    } else {
      return tripData.tripstats;
    }
  }

  private getX(group: string) {
    const values = {
      device: 'registration',
      service: 'label',
      month: 'time_value',
      week: 'time_value',
      day: 'time_value',
    };
    return values[group];
  }

  public setColor(type: GraphVariable, data: number[]): string[] | string {
    if (type === 'eco_avg') {
      const lastValue = data?.[data?.length - 1]?.['data'];
      return this.setColorEco(lastValue);
    }

    return this.colorsGraphAlpha[type];
  }

  private setColorEco(data: number[]): string[] | string {
    const resultColors: string[] = [];
    if (data?.length > 0) {
      for (const d of data) {
        if (data[d] >= 85) {
          // GREEN
          resultColors.push('rgba(204, 234, 187, 1)');
        } else if (data[d] >= 50) {
          // YELLOW
          resultColors.push('rgba(253, 219, 58, 1)');
        } else {
          // ORANGE
          resultColors.push('rgba(250, 165, 66, 1)');
        }
      }
      return resultColors;
    } else {
      return 'rgba(167, 219, 138, 0.4)';
    }
  }

  public setChartGUI() {
    //save uid_service clicked by the user
    if (this.tripStatsService.chosenService !== this.config.uid_service) {
      this.tripStatsService.chosenService = this.config.uid_service;
      //Get list of subservices
      this.tripStatsService.getSubservices(this.tripStatsService.chosenService);
    }

    this.chartOptions.scales.y.ticks.callback = (value, index, values) => {
      let units: string = this.unitsGraph[this.config.y];
      if (this.config.y === 'engine') {
        units = this.unit;
      }
      return units ? value + units : value;
    };
    const medianTrad = this.translateService.instant('CHART.MEDIAN');

    this.chartOptions.plugins.tooltip.callbacks.label = tooltipItem => {
      if (tooltipItem.dataset.type === 'line') {
        return medianTrad + ' : ' + tooltipItem.parsed.y;
      }
      return tooltipItem.parsed.y + this.unitsGraph[this.config.y];
    };
  }

  private getTitleOnChart(labels: string[][], config: DashboardCard) {
    this.addMedian();
    if (labels.length > 0) {
      if (config.group !== 'device' && config.group !== 'service') {
        switch (config.period) {
          case 'WR':
          case 'WL':
            this.chartOptions.plugins.tooltip.callbacks.label = function (tooltipItem) {
              return '';
            };
            break;
          case 'MR':
          case 'ML':
          case 'M3':
            this.chartOptions.plugins.tooltip.callbacks.title = function (tooltipItem) {
              if (tooltipItem[0].dataset.type === 'line') {
                return '';
              }
              return tooltipItem[0].label;
            };
            break;
        }
      } else {
        this.chartOptions.plugins.tooltip.callbacks.title = function (tooltipItem) {
          if (tooltipItem[0].dataset.type === 'line') {
            return '';
          }
          return tooltipItem[0].label;
        };
      }
    }
  }

  private getFooterOnChart(config: DashboardCard, tripStats: TripStats) {
    const tooltipServices = config.group === 'device' ? this.getService(tripStats.uid_device) : [];

    if (
      tooltipServices.length > 1 &&
      config.group !== 'month' &&
      config.group !== 'week' &&
      config.group !== 'day'
    ) {
      this.chartOptions.plugins.tooltip.callbacks.footer = function (tooltipItem) {
        if (tooltipItem[0].dataset.type === 'line') {
          return '';
        }
        return tooltipServices[tooltipItem[0].dataIndex] + '';
      };
    } else {
      this.chartOptions.plugins.tooltip.callbacks.footer = function (tooltipItem) {
        return '';
      };
    }
  }

  public selectService() {
    if (this.changingService === false) {
      this.changingService = true;
      this.routingService.popUpEvent('selectService' + this.index.toString(), this.closePopUp);
      setTimeout(() => {
        if (document.getElementById(String(this.config.uid_service))) {
          document.getElementById(String(this.config.uid_service)).scrollIntoView();
        }
      });
    } else {
      this.changingService = false;
    }
  }

  private closePopUp = () => {
    window.removeEventListener('click', this.routingService.actionEvent);
    this.changingService = false;
  };

  public changeService(uid: number) {
    this.config.uid_service = uid;
    this.changingService = false;
    if (this.config.group === 'service') {
      const chartStats = this.tripstatsStore.$tripstats()[this.index].servicedata?.[uid];
      if (!chartStats) this.noData = true;
      // Same duck-typed reuse as selectTripStats(): a per-service entry only carries the
      // fields this view actually reads (mileage, stops, engine, ...).
      this.tripstatsStore.mutate(ts => {
        ts[this.index].tripstats = chartStats as unknown as TripStats;
      });
      this.chartData = {
        labels: chartStats[this.getX(this.config.group)],
        datasets: [
          {
            data: chartStats[this.config.y],
            label: this.labelsGraph[this.config.y],
          },
        ],
      };
      this.noData =
        this.chartData.datasets[0].data.length < 1 ||
        (this.chartData.datasets[0].data.length === 1 && this.chartData.datasets[0].data[0] === 0);

      if (!this.noData) {
        this.preferenceService.mutatePreference(preference => {
          preference.dashboard.splice(this.index, 1, this.config);
        });
        this.preferenceService.saveDraggedPreferences('dashboard', true, false);
      }
    }
    this.updateData();
  }

  public resetServices(event: boolean) {
    this.config.uid_service = undefined;
    this.changingService = false;
    this.updateData();
  }

  public destroy() {
    this.preferenceService.destroyChart(this.index);
  }

  public setPeriodEvent(data: DashboardCard) {
    this.config.period = data.period;
    this.setPeriod(false);
  }

  public setPeriod(init?: boolean) {
    let from = new Date();
    from.setHours(0, 0, 0, 0);
    let to = new Date();
    to.setHours(23, 59, 0, 0);
    switch (this.config.period) {
      case 'M3':
        from.setMonth(from.getMonth() - 3);
        from.setDate(1);
        to.setMonth(to.getMonth() - 1);
        let last = this.lastday(to.getFullYear(), to.getMonth());
        to.setDate(last);
        break;
      case 'MR':
        from.setDate(1);
        break;
      case 'ML':
        from.setMonth(from.getMonth() - 1);
        from.setDate(1);
        to.setMonth(to.getMonth() - 1);
        const lastDay = this.lastday(to.getFullYear(), to.getMonth());
        to.setDate(lastDay);
        break;
      case 'WR':
        from = this.getMonday(from);
        break;
      case 'WL':
        from = this.getMonday(from);
        from.setDate(from.getDate() - 7);
        to = this.getMonday(to);
        to.setDate(to.getDate() - 1);
    }
    this.from = from.valueOf() / 1000;
    this.to = to.valueOf() / 1000;
    this.updateData(init);
  }

  private getParallelPeriod(period: string, fromStart: number, toStart: number): number[] {
    const from = new Date(fromStart * 1000);
    const to = new Date(toStart * 1000);
    switch (period) {
      case 'M3':
        const last = this.lastday(from.getFullYear(), from.getMonth() - 1);
        to.setMonth(from.getMonth() - 1);
        to.setDate(last);
        from.setMonth(from.getMonth() - 3);
        from.setDate(1);
        break;
      case 'MR':
        to.setMonth(to.getMonth() - 1);
        from.setMonth(from.getMonth() - 1);
        break;
      case 'ML':
        const lastDay = this.lastday(from.getFullYear(), from.getMonth() - 1);
        to.setDate(lastDay);
        to.setMonth(from.getMonth() - 1);
        from.setMonth(from.getMonth() - 1);
        break;
      case 'WR':
        from.setDate(from.getDate() - 7);
        to.setDate(to.getDate() - 7);
        break;
      case 'WL':
        from.setDate(from.getDate() - 7);
        to.setDate(to.getDate() - 7);
    }
    return [from.valueOf() / 1000, to.valueOf() / 1000];
  }

  private getPercent(stats: TripStats) {
    const engine = stats.engine;
    const diff = this.to - this.from;
    const hours = Math.round(diff / 3600);
    let newEngine;

    if (this.config.group === 'device') {
      newEngine = engine.map(data => Math.round(data / (hours / 100)));
    } else if (this.config.group === 'month' || this.config.group === 'week' || this.config.group === 'day') {
      if (this.config.uid_service === undefined) {
        this.tripStatsService.chosenService = 0;
      }
      this.tripStatsService.allSubservicesDevices = [];
      this.tripStatsService.getSubservicesDevices(this.tripStatsService.chosenService);
      const number = this.tripStatsService.allSubservicesDevices.length;
      const totalHour = hours * number;
      const percent = totalHour / 100;
      newEngine = engine.map(data => Math.round(data / percent));
    } else if (this.config.group === 'service') {
      if (stats.uid_service.length !== 0) {
        const result = [];
        if (this.config.uid_service === undefined) {
          this.tripStatsService.chosenService = 0;
        }
        this.tripStatsService.allSubservicesDevices = [];
        this.tripStatsService.getSubservicesDevices(this.tripStatsService.chosenService);
        const number = this.tripStatsService.allSubservicesDevices.length;

        for (const service of stats.uid_service) {
          const totalHour = hours * number;
          const percent = totalHour / 100;
          result.push(Math.round(engine[stats.uid_service.indexOf(service)] / percent));
        }
        newEngine = result;
      } else {
        const number = this._unitsService.getDevices(this.config.uid_service, {
          directChildrenOnly: true,
        }).length;
        const percent = (hours * number) / 100;
        newEngine = engine.map(data => Math.round(data / percent));
      }
    }
    return newEngine;
  }

  public updateUnit(event: string) {
    this.unit = event;
    this.unitsGraph.engine = ' ' + this.unit;
    this.updateData();
  }

  private lastday(year: number, month: number) {
    return new Date(year, month + 1, 0).getDate();
  }

  private getMonday(d: Date) {
    const day = d.getDay();
    const diff = d.getDate() - day + (day == 0 ? -6 : 1);
    return new Date(d.setDate(diff));
  }

  public resize(value: boolean) {
    this.tripStatsService.resizeChart(value, this.index, this.config);
  }

  public getState(index: number) {
    this.resize(true);
    this.tripStatsService.$isEditChart.set(false);
    setTimeout(() => this.displayState(index), 800);
  }

  public editChart() {
    this.resize(true);
    this.tripStatsService.$isEditChart.set(true);
  }

  public displayState(index) {
    this.tripStatsService.$isEditChart.set(false);
    const canva = <HTMLCanvasElement>document.getElementById('myCanvas' + index);
    const img = canva.toDataURL('image/png');
    const serviceDisplayed =
      this.config.uid_service == null
        ? this.translateService.instant('CHART.GLOBAL_FLEET')
        : this._unitsService.$unitsById()[this.config.uid_service].label;
    const titleForCSV = `${this.labelsGraph[this.config.y]}_${serviceDisplayed}_${this.breaking[this.config.group]}_${this.getDateToString(this.from)}_${this.getDateToString(this.to)}.csv`;

    let style = '<style>';
    style =
      style +
      'body {width: 100%;font: 17px Calibri; text-align: center; margin: 0px; margin-top: 10px; margin-bottom: 10px;}';
    style = style + 'h4 {display: inline-block; width: 50%;}';
    style = style + 'table {width: 95%;font: 17px Calibri; margin: auto;}';
    style = style + 'table, th, td {border: solid 1px #DDD; border-collapse: collapse;';
    style = style + 'padding: 2px 3px;text-align: center;}';
    style = style + 'th {background-color: #1b3158; color: white}';
    style =
      style +
      ".material-icons {font-family: 'Material Icons';font-style: normal;font-size: 30px;-webkit-font-smoothing: antialiased;text-rendering: optimizeLegibility;-moz-osx-font-smoothing: grayscale;font-feature-settings: 'liga';}";
    style =
      style +
      '#print {border-radius: 50%; width:40px; height: 40px; justify-content: center;position: fixed;left: calc(100% - 50px);top: calc(100% - 50px); cursor: pointer;}';
    style = style + '</style>';

    const win = window.open('/', '', 'height=700,width=700');
    win.document.write('<title>' + titleForCSV + '</title>');
    win.document.write(style);
    win.document.write(
      `<div><p style="color:#1b3158; font-size: 24px; font-family: var(--mat-sys-plain-font-family); font-weight: var(--mat-sys-bold-font-weight); margin-left: 10px;">Coyote Fleet Connect</p></div>`
    );
    win.document.write(
      `<h1>${this.labelsGraph[this.config.y]} ${this.translateService.instant('COMMON.OF')} ${serviceDisplayed}</h1>`
    );
    win.document.write('<br>');
    win.document.write('<img src="' + img + '" style="width: 85%;"/>');
    win.document.write('<div style="page-break-after: always;">');
    if (!this.config.compare) {
      win.document.write(
        `<h4>${this.translateService.instant('DATE.PERIOD')} : ${this.getDateToString(this.from)} ${this.translateService.instant('TABLE.TO')} ${this.getDateToString(this.to)} </h4>`
      );
    }

    win.document.write(
      '<h4> ' +
        this.translateService.instant('CHART.RUPTURE_BY') +
        ' : ' +
        this.breaking[this.config.group] +
        '</h4>'
    );
    win.document.write('</div>');
    win.document.write('<br>');
    win.document.write('<table>');
    win.document.write('<tr>');
    win.document.write('<th>' + this.breaking[this.config.group] + '</th>');
    if (this.config.compare) {
      for (let i = 0; i < this.chartData.datasets.length; i++) {
        if (this.chartData.datasets[i].label !== 'median') {
          win.document.write('<th>' + this.chartData.datasets[i].label + '</th>');
        }
      }
    } else {
      win.document.write('<th>' + this.labelsGraph[this.config.y] + '</th>');
    }
    win.document.write('</tr>');
    for (const c in this.chartData.labels) {
      win.document.write('<tr>');
      win.document.write('<td>' + this.chartData.labels[c] + '</td>');
      for (let i = 0; i < this.chartData.datasets.length; i++) {
        if (this.chartData.datasets[i].label !== 'median') {
          win.document.write(
            '<td>' + this.chartData.datasets[i].data[c] + this.unitsGraph[this.config.y] + '</td>'
          );
        }
      }
      win.document.write('</tr>');
    }
    win.document.write('</table>');
    setTimeout(() => {
      win.print();
      win.close();
    }, 600);
  }

  public exportGraph() {
    const serviceDisplayed =
      !this.config.uid_service || this.config.uid_service === null
        ? this.translateService.instant('CHART.GLOBAL_FLEET')
        : this._unitsService.$unitsById()[this.config.uid_service].label;
    const dataToExport = this.deviceSaveListService.getGraphData(
      this.labelsGraph[this.config.y],
      serviceDisplayed,
      this.breaking[this.config.group],
      this.chartData.labels,
      this.chartData.datasets as { data: number[]; label: string }[],
      this.unitsGraph[this.config.y],
      this.getDateToString(this.from),
      this.getDateToString(this.to),
      this.config.compare
    );

    const titleForCSV = `${this.labelsGraph[this.config.y]}_${serviceDisplayed}_${this.breaking[this.config.group]}_${this.getDateToString(this.from)}-${this.getDateToString(this.to)}.csv`;

    this.deviceSaveListService.exportToCSV(dataToExport, titleForCSV);
  }

  private getDateToString(date: number): string {
    return this._dynamicDatePipe.transform(new Date(date * 1000));
  }

  private addMedian() {
    if (
      this.chartData.datasets?.length === 1 &&
      !this.config?.compare &&
      this.chartData.datasets[0].data?.length > 0
    ) {
      const datas = this.chartData.datasets[0].data;
      this.chartData.datasets.push({
        data: this.addItemsMedian(this.calculateMedian(datas), datas?.length),
        borderColor: '#FF0000',
        label: 'median',
        type: 'line',
        borderWidth: 0.5,
        tension: 0.4,
        pointRadius: 0,
        pointHoverRadius: 0,
        pointBorderWidth: 2,
        pointHoverBorderWidth: 0,
        pointBackgroundColor: '#FF0000',
        pointHoverBackgroundColor: 'transparent',
        order: 0,
      });
    } else if (this.config?.compare) {
      const index = this.chartData.datasets.findIndex(d => d?.label === 'median');
      if (index > 0) {
        this.chartData.datasets.splice(index, 1);
      }
    }
  }

  private addItemsMedian(value: number, length: number): number[] {
    return Array(length).fill(value);
  }

  private calculateMedian(array: Array<any>): number {
    const data = [...array];
    data.sort((a, b) => a - b);
    const length = data.length;
    const middle = Math.floor(length / 2);

    if (length % 2 === 1) {
      return data[middle];
    } else {
      const middle1 = data[middle - 1];
      const middle2 = data[middle];
      return (middle1 + middle2) / 2;
    }
  }

  public sortData() {
    const combined = this.chartData.labels.map((label, index) => ({
      label: label,
      value: Number(this.chartData.datasets[0].data[index]),
    }));

    const sorted = this.isSortedData
      ? combined.sort((a, b) => b.value - a.value)
      : combined.sort((a, b) => a.value - b.value);

    this.chartData.labels = sorted.map(item => item.label);
    this.chartData.datasets[0].data = sorted.map(item => item.value);
    this.chart.update();
    this.isSortedData = !this.isSortedData;
  }
}
